import { faBagShopping } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { MonthPicker } from '@shared/components/ui/date-picker';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Skeleton } from '@shared/components/ui/shadcn/skeleton';
import { format, parse } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CUSTOMER_ORIGIN_VALUES } from '../../constants';
import { useMarketingData, useMarketingFilters } from '../../hooks';
import { MarketingFilters } from '../../models';
import { Progress } from '../interaction/Progress';

const SEGMENT_COLORS = ['#6366F1', '#60A5FA', '#34D399', '#FBBF24', '#F87171'];

interface LeadTrackingTableProps {
  filters?: MarketingFilters;
}

export const LeadTrackingTable = ({ filters }: LeadTrackingTableProps = {}) => {
  const { t } = useTranslation(['marketingModule', 'createEditClientsModule']);

  const { refDate, updateFilters } = useMarketingFilters();

  const selectedDate = useMemo(() => {
    if (refDate) {
      try {
        return parse(`${refDate}-01`, 'yyyy-MM-dd', new Date());
      } catch {
        return undefined;
      }
    }
    return undefined;
  }, [refDate]);

  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      const monthString = format(date, 'yyyy-MM');
      updateFilters({ refDate: monthString });
    }
  };

  const [selectedSource, setSelectedSource] = useState<string>('organic');

  const sourceOptions = useMemo(
    () =>
      CUSTOMER_ORIGIN_VALUES.map((origin) => ({
        label: t('createEditClientsModule:customerOrigins.' + origin, origin),
        value: origin,
      })),
    [t],
  );

  const dynamicFilters: MarketingFilters = {
    ...filters,
    customer_origin: [selectedSource],
    ...(refDate && { ref_date: refDate }),
  };

  const { segments, meta, isLoading } = useMarketingData({ filters: dynamicFilters });

  const trackingData = React.useMemo(() => {
    if (!segments || segments.length === 0) return [];

    const keys = ['zero', 's_1_500', 's_501_2000', 's_2001_5000', 's_gt_5001'] as const;
    const ranges = ['0', '1–500', '501–2.000', '2.001–5.000', '≥ 5.001'];

    return keys.map((key, index) => ({
      range: ranges[index],
      companies: segments.find((s) => s.key === key)?.total_clients || 0,
      percentage: segments.find((s) => s.key === key)?.percentage || 0,
      color: SEGMENT_COLORS[index],
    }));
  }, [segments]);

  return (
    <SectionBox data-testid="marketing-lead-tracking" className="h-full">
      <div className="space-y-5">
        <h2 className="text-lg font-semibold text-title">
          {t('leadTracking.title', 'Seguimiento de Leads por Fuente')}
        </h2>

        {isLoading ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={`filter-skeleton-${index}`} className="space-y-2">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="grid grid-cols-3 gap-4 p-3 border-b border-border bg-muted/30">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-18" />
              </div>
              {Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={`lead-skeleton-${index}`}
                  className="grid grid-cols-3 gap-4 p-3 border-b border-border/50 last:border-b-0"
                >
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-end sm:gap-x-3 sm:gap-y-2">
              <div className="w-full min-w-0 sm:w-[160px] sm:max-w-[180px]">
                <label className="mb-2 block text-[11px] font-medium leading-none text-muted-foreground">
                  {t('leadTracking.filters.firstEntry', 'Fecha 1ª entrada')}
                </label>
                <div className="h-9 flex items-center px-3 border border-border rounded-md text-sm bg-background text-foreground">
                  {meta?.month_first_entry || t('leadTracking.filters.noData', 'Sin datos')}
                </div>
              </div>
              <div className="w-full min-w-0 sm:w-[160px] sm:max-w-[180px]">
                <label className="mb-2 block text-[11px] font-medium leading-none text-muted-foreground">
                  {t('leadTracking.filters.reportDate', 'Fecha reporte')}
                </label>
                <MonthPicker
                  date={selectedDate}
                  onDateChange={handleDateChange}
                  placeholder={t('leadTracking.filters.selectMonth', 'Seleccionar mes')}
                  className="h-9 border-border bg-background"
                  data-testid="marketing-lead-tracking-month"
                />
              </div>
              <div className="w-full min-w-0 sm:w-[160px] sm:max-w-[180px]">
                <label className="mb-2 block text-[11px] font-medium leading-none text-muted-foreground">
                  {t('leadTracking.filters.source', 'Fuente')}
                </label>
                <Combobox
                  options={sourceOptions}
                  value={selectedSource}
                  setValue={setSelectedSource}
                  placeholder={t('leadTracking.filters.source', 'Fuente')}
                  buttonClassName="h-9 text-sm border-border bg-background"
                  dataTestId="marketing-lead-tracking-source"
                  translations={{ notFound: t('filters.notFound', 'Sin resultados') }}
                />
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                      {t('leadTracking.headers.ordersPerMonth', 'Pedidos / Mes')}
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                      {t('leadTracking.headers.companies', 'Nº Empresas')}
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground min-w-[140px]">
                      {t('leadTracking.headers.percentage', 'Porcentaje')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {trackingData.map((row, index) => (
                    <tr
                      key={index}
                      className="border-b border-border/50 last:border-b-0 hover:bg-muted/30 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: row.color }}
                          />
                          <FontAwesomeIcon icon={faBagShopping} className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="font-medium text-sm text-foreground">{row.range}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm font-semibold text-foreground tabular-nums">
                          {row.companies.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <Progress
                            value={row.percentage}
                            className="flex-1 max-w-[100px]"
                            indicatorClassName="bg-transparent"
                            indicatorStyle={{ backgroundColor: row.color }}
                          />
                          <span className="text-sm text-muted-foreground tabular-nums w-12 text-right">
                            {row.percentage}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </SectionBox>
  );
};
